import React, { useState, useEffect, useMemo, useCallback, useContext, useRef, Suspense, lazy } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import FieldLabel from 'shared/forms/field-label'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faPencilAlt, faPalette, faArrowUp, faArrowDown, faTrash, faFilter } from '@fortawesome/free-solid-svg-icons'
import ConfirmDelete from 'shared/confirm-delete'
import ChartBrandingModal from './chart-branding-modal'
import { useTableColumns } from 'api/hooks/use-table-columns'
import useMunicipalitySettings from 'api/hooks/use-municipality-settings'
import { useAnalytics } from 'api/hooks/use-analytics'
import { pickPresentParams, prepParams } from 'helpers/utils'
import { useCreateExport } from 'api/hooks/use-exports'
import { toast } from 'react-toastify'
import {
  useCreateDashboardChart,
  useUpdateDashboardChart,
  useDestroyDashboardChart
} from 'api/hooks/use-dashboard-charts'
import useCharts from 'api/hooks/use-charts'
import LineChart from 'charts/line-chart'
import VerticalBarChart from 'charts/vertical-bar-chart'
import BarChart from 'charts/bar-chart'
import PieDonutChart from 'charts/pie-donut-chart'
import StackedAreaChart from 'charts/stacked-area-chart'
import PivotTable from 'charts/pivot-table'
import DataTable from 'charts/data-table'
import ScatterPlot from 'charts/scatter-plot'
import KpiChart from 'charts/kpi-chart'
import TextChart from 'charts/text-chart'
import HeaderChart from 'charts/header-chart'
import PageBreakChart from 'charts/page-break-chart'
import ChartErrorBoundary from 'shared/chart-error-boundary'
import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'
import { transactionFilterParams, viewFilters } from 'api/transaction-query-params'
import { parseFilterTree, requestFilterTree } from 'api/filter-tree'
import ChartFilterGroups from './filter-groups/ChartFilterGroups'
import { appliedViewFilters } from './filter-groups/applied-filters'
import { buildFilterColumns } from './filter-groups/filter-columns'
import { countFilters, startingTree } from './filter-groups/tree'
import { describeTree, outlineTree } from './filter-groups/summary'
import ChartFilterSummary from './filter-groups/ChartFilterSummary'
import AppBadge from 'shared/badges/app-badge'
import customColumnService from 'services/customColumnService'
import AuthContext from 'context/auth-context'
import {
  formatValueByColumn,
  createTooltipFormatter,
  getAggregationLabel,
  formatDateTimeValue
} from '../helpers/chart-formatters'
import UserContext from 'context/user-context'
import { SEGMENT_DISPLAY_DEFAULT, DATE_FORMAT_DEFAULT } from 'charts/chart-options'
import { getColumnDisplayName } from './utils'
import { useActiveAccountGrouping } from 'api/hooks/use-account-groupings'
import {
  validateChartForm,
  chartTypeNeedsDataSource,
  isChartQueryEnabled,
  buildChartParams,
  buildQueryParams,
  formatDateLabels,
  supportsDateFormatting,
  renderChartOptions,
  sanitizeChartParams
} from 'charts/chart-type-registry'
import { ChartTypeOption, ChartTitleOption, DataSourceOption } from 'charts/chart-options'
import { getChartBrandingColors } from 'utils/chart-branding'
import { useDashboard } from 'api/hooks/use-dashboards'
import { useQuery, useQueryClient } from 'react-query'
import { aggregationOptions, measureColumns, defaultMeasureColumn } from 'utils/column-aggregation'

// Default data/mutation wiring for dashboard charts. Reports pass a parallel adapter
// (see report-grid-row.jsx) so this component can be reused without forking. Existing
// dashboard call sites pass no adapter and therefore behave exactly as before.
export const DASHBOARD_CHART_ADAPTER = {
  useChartData: ({ containerId, chartId, params, options }) =>
    useCharts({ dashboardId: containerId, chartId, params, options }),
  useCreateChart: ({ containerId, options }) => useCreateDashboardChart({ id: containerId, options }),
  useUpdateChart: ({ containerId, options }) => useUpdateDashboardChart({ dashboardId: containerId, options }),
  useDestroyChart: ({ containerId, chartId, options }) =>
    useDestroyDashboardChart({ dashboardId: containerId, id: chartId, options }),
  buildPayload: ({ chartId, containerId, chartType, title, params, gridRowId, gridColumnPosition, extra }) => ({
    id: chartId,
    dashboard_id: containerId,
    chart_type: chartType,
    title,
    params,
    ...(extra || {}),
    ...(gridRowId && { dashboard_grid_row_id: gridRowId }),
    ...(gridColumnPosition !== null && gridColumnPosition !== undefined && {
      grid_column_position: gridColumnPosition
    })
  }),
  invalidate: (queryClient, containerId) => {
    queryClient.invalidateQueries([`dashboards/${containerId}/charts`])
    queryClient.invalidateQueries([`dashboards/${containerId}/grid_rows`])
    queryClient.invalidateQueries(['dashboard-chart-data', containerId])
  },
  allowCsvExport: true,
  // Clicking a point lists its transactions (Charts::Drilldown); reports have no View grid to show.
  drillDown: true,
  // Endpoint the pivot/data-table use for pagination + sort ("load more").
  chartDataPath: (chartId) => `charts/${chartId}/data`
}

// Lazy, so the transaction grid loads on the first drill-down rather than with every dashboard.
const ChartDrilldownModal = lazy(() => import('./chart-drilldown-modal'))

const DRILLABLE_CHART_TYPES = [
  'line', 'vertical-bar', 'horizontal-bar', 'stacked-area', 'pie-donut', 'kpi', 'pivot-table', 'data-table',
  'scatter-plot'
]

// The key a pie slice drills by: the API sends a NULL slice as an empty key, labelled None.
const pieSliceKey = (key) => (key === '' || key === 'undefined' || key == null ? 'None' : key)

// A text or header block is not a chart, so the config modal names what it actually edits.
const MODAL_TITLES = {
  header: 'Header Options',
  'page-break': 'Page Break',
  text: 'Text Options'
}

function ChartCard({
  dashboardId,
  sectionId,
  params,
  chart,
  onSaved,
  autoOpenModal = false,
  onCancel,
  gridRowId = null,
  gridColumnPosition = null,
  canEdit = false,
  isPreviewMode = false,
  adapter = DASHBOARD_CHART_ADAPTER,
  containerId,
  extraPayload = {},
  // Report-builder only: the linked data source (View) this chart pulls from, a path to open it,
  // and whether that source has been removed from the report. Dashboards leave these null/false
  // (no such concept).
  dataSourceLabel = null,
  dataSourceHref = '',
  dataSourceMissing = false,
  // Report-builder only: the body carries a table, so it has no safely editable text.
  isComputed = false,
  // Report-builder only: when a computed block's figures were calculated, since it is never recomputed.
  staticFiguresNotice = null,
  // Report-builder only: those figures were computed under a grouping the report has left.
  groupingStaleNotice = null,
  // Report-builder only: every data source the report has linked ({ id, label, sectionId }) plus
  // the one this chart is bound to, so the options modal can re-point a chart whose source was
  // removed. Dashboards pass neither, which leaves the data source read-only.
  dataSources = [],
  dataSourceId = null,
  isSelected = false,
  onSelect = null,
  onMoveUp = null,
  onMoveDown = null,
  isFirst = false,
  isLast = false,
  // The View whose grouping this chart buckets by — its own, or for a report chart the View
  // behind its dataset, passed explicitly since it has no dashboardId.
  groupingDashboardId = null,
  // Report-builder only: outranks the View above, as it does server-side.
  groupingId = null
}) {
  const resolvedContainerId = containerId ?? dashboardId
  const { useChartData, useCreateChart, useUpdateChart, useDestroyChart } = adapter
  const queryClient = useQueryClient()
  const { municipality } = useContext(UserContext)
  // Column dropdowns, axis labels and headers all name the taxonomy (getColumnDisplayName).
  const groupingName =
    useActiveAccountGrouping(groupingDashboardId ?? dashboardId, groupingId)?.name || null
  // Bound to that grouping so editors need no second argument. Memoized — several hold it in
  // deps.
  const resolveColumnName = useCallback(
    (column, muni) => getColumnDisplayName(column, muni, groupingName),
    [groupingName]
  )
  // The data source in effect (saved) and the one being edited in the modal (draft), mirroring
  // every other saved/draft pair below. Empty string when the chart is unbound — which is what a
  // removed data source leaves behind.
  const [savedDataSourceId, setSavedDataSourceId] = useState(dataSourceId || '')
  const [draftDataSourceId, setDraftDataSourceId] = useState(dataSourceId || '')
  // The chart's own AND/OR filter groups: saved as the JSON string the API stores, edited as a tree.
  const [savedChartFilterTree, setSavedChartFilterTree] = useState(chart?.filter_tree || null)
  const [chartFilterDraft, setChartFilterDraft] = useState(null)
  useEffect(() => {
    setSavedChartFilterTree(chart?.filter_tree || null)
  }, [chart?.filter_tree])
  // Whether the chart prints its own filters under itself, so an export says why it covers less.
  const [savedShowFilterCaption, setSavedShowFilterCaption] = useState(!!chart?.show_filter_caption)
  const [draftShowFilterCaption, setDraftShowFilterCaption] = useState(!!chart?.show_filter_caption)
  useEffect(() => {
    setSavedShowFilterCaption(!!chart?.show_filter_caption)
  }, [chart?.show_filter_caption])
  // The card outlives a change to its binding (it isn't keyed by chart id), so follow the prop
  // rather than stranding state from before the data source was removed or re-pointed.
  useEffect(() => {
    setSavedDataSourceId(dataSourceId || '')
    setDraftDataSourceId(dataSourceId || '')
  }, [dataSourceId])
  // Columns and analytics belong to the data source's section, so the picker has to drive them:
  // choosing a source from another section must reload both before the user picks columns.
  // Falls back to the sectionId prop, which is all a dashboard chart ever has.
  const resolvedSectionId =
    dataSources.find((source) => source.id === draftDataSourceId)?.sectionId || sectionId
  // Only the report builder hands over a list to pick from, and only an editor may re-point a
  // chart. Everywhere else the data source stays the read-only line it has always been.
  const canPickDataSource = dataSources.length > 0 && canEdit
  const { data: columns = [], isSuccess: isColumnsSuccess } = useTableColumns({
    // Naming the data source lets the endpoint offer the Review dimensions when it is a sample; the
    // server verifies that rather than trusting the claim. Follows the draft so picking a sample in
    // the modal reveals them before saving.
    params: draftDataSourceId
      ? { section_id: resolvedSectionId, report_dataset_id: draftDataSourceId }
      : { section_id: resolvedSectionId },
    options: {}
  })
  const { data: municipalitySettings } = useMunicipalitySettings({
    options: {}
  })
  const isNewHeaderChart = !chart?.id && (chart?.chart_type === 'header' || !chart?.chart_type)
  const { data: dashboard } = useDashboard({
    id: dashboardId,
    options: { enabled: !!dashboardId && isNewHeaderChart }
  })
  const { data: analytics = [] } = useAnalytics({
    params: { section_id: resolvedSectionId, perPage: 1000 },
    options: { enabled: !!resolvedSectionId }
  })

  const [showModal, setShowModal] = useState(autoOpenModal)
  useEffect(() => {
    if (showModal) setChartFilterDraft((draft) => draft ?? startingTree(savedChartFilterTree))
  }, [showModal, savedChartFilterTree])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteFromModal, setDeleteFromModal] = useState(false)
  const [showBrandingModal, setShowBrandingModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportName, setExportName] = useState('')

  // Saved state (used for chart rendering)
  const [savedChartType, setSavedChartType] = useState(
    chart?.chart_type || 'line'
  )
  const [savedGroupBy, setSavedGroupBy] = useState(
    chart?.params?.group_by_column_id ||
      chart?.params?.pivot_row_column_id ||
      ''
  )
  const [savedGroupBy2, setSavedGroupBy2] = useState(
    chart?.params?.group_by2_column_id ||
      chart?.params?.pivot_column_column_id ||
      ''
  )
  const [savedTitle, setSavedTitle] = useState(chart?.title || '')
  const [savedXAxisColumn, setSavedXAxisColumn] = useState(
    chart?.params?.x_axis_column_id || ''
  )
  const [savedXAxisDateGrouping, setSavedXAxisDateGrouping] = useState(
    chart?.params?.x_axis_date_grouping || 'day'
  )
  const [savedPivotRowDateGrouping, setSavedPivotRowDateGrouping] = useState(
    chart?.params?.pivot_row_date_grouping || 'day'
  )
  // How an account-segment row grouping reads: its description, its code, or both.
  const [savedPivotRowSegmentDisplay, setSavedPivotRowSegmentDisplay] = useState(
    chart?.params?.pivot_row_segment_display || SEGMENT_DISPLAY_DEFAULT
  )
  // How a date row grouping reads: "July 30, 2026", "07/30/2026", ...
  const [savedPivotRowDateFormat, setSavedPivotRowDateFormat] = useState(
    chart?.params?.pivot_row_date_format || DATE_FORMAT_DEFAULT
  )
  const [savedYAxisColumn, setSavedYAxisColumn] = useState(
    chart?.params?.y_axis_column_id || ''
  )
  const [savedYAxisAggregation, setSavedYAxisAggregation] = useState(
    chart?.params?.y_axis_aggregation || 'sum'
  )
  const [savedYAxisLabel, setSavedYAxisLabel] = useState(
    chart?.params?.y_axis_label || ''
  )
  const [savedYAxisGroupBy, setSavedYAxisGroupBy] = useState(
    chart?.params?.y_axis_group_by_column_id || ''
  )
  const [savedDisplayLimit, setSavedDisplayLimit] = useState(
    chart?.params?.display_limit ?? ''
  )
  const [savedShowTotals, setSavedShowTotals] = useState(
    chart?.params?.show_totals || false
  )
  const [savedValueColumn, setSavedValueColumn] = useState(
    chart?.params?.slice_column_id || chart?.params?.value_column_id || ''
  )
  const [savedAggregationMethod, setSavedAggregationMethod] = useState(
    chart?.params?.slice_aggregation ||
      chart?.params?.aggregation_method ||
      'sum'
  )
  const [savedKpiValueColumn, setSavedKpiValueColumn] = useState(
    chart?.params?.value_column_id || ''
  )
  const [savedKpiAggregation, setSavedKpiAggregation] = useState(
    chart?.params?.aggregation_method || 'sum'
  )
  const [savedKpiAnalytic, setSavedKpiAnalytic] = useState(
    chart?.params?.has_no_analytics ? 'none' : (chart?.params?.analytic_id || '')
  )
  const [savedBrandingType, setSavedBrandingType] = useState(
    chart?.branding_type || ''
  )
  const [savedBrandingColor, setSavedBrandingColor] = useState(
    chart?.branding_color || ''
  )
  const [savedTextContent, setSavedTextContent] = useState(chart?.params?.text_content || '')
  const [savedTextRepeatOnEveryPage, setSavedTextRepeatOnEveryPage] = useState(
    chart?.params?.repeat_on_every_page || false
  )


  // Reference to the chart instance for programmatic export
  const chartRef = useRef(null)
  // Reference to the card DOM element for html2canvas fallback capture
  const cardRef = useRef(null)


  // Draft state (used in modal form - only applies when modal is open)
  const [chartType, setChartType] = useState(chart?.chart_type || 'line')
  const [groupBy, setGroupBy] = useState(
    chart?.params?.group_by_column_id ||
      chart?.params?.pivot_row_column_id ||
      ''
  )
  const [groupBy2, setGroupBy2] = useState(
    // eslint-disable-line no-unused-vars
    chart?.params?.group_by2_column_id ||
      chart?.params?.pivot_column_column_id ||
      ''
  )
  const [title, setTitle] = useState(chart?.title || '')
  const [xAxisColumn, setXAxisColumn] = useState(
    chart?.params?.x_axis_column_id || ''
  )
  const [xAxisDateGrouping, setXAxisDateGrouping] = useState(
    chart?.params?.x_axis_date_grouping || 'day'
  )
  const [pivotRowDateGrouping, setPivotRowDateGrouping] = useState(
    chart?.params?.pivot_row_date_grouping || 'day'
  )
  const [pivotRowSegmentDisplay, setPivotRowSegmentDisplay] = useState(
    chart?.params?.pivot_row_segment_display || SEGMENT_DISPLAY_DEFAULT
  )
  const [pivotRowDateFormat, setPivotRowDateFormat] = useState(
    chart?.params?.pivot_row_date_format || DATE_FORMAT_DEFAULT
  )
  const [yAxisColumn, setYAxisColumn] = useState(
    chart?.params?.y_axis_column_id || ''
  )
  const [yAxisAggregation, setYAxisAggregation] = useState(
    chart?.params?.y_axis_aggregation || 'sum'
  )
  const [yAxisLabel, setYAxisLabel] = useState(
    chart?.params?.y_axis_label || ''
  )
  const [yAxisGroupBy, setYAxisGroupBy] = useState(
    chart?.params?.y_axis_group_by_column_id || ''
  )
  const [displayLimit, setDisplayLimit] = useState(
    chart?.params?.display_limit ?? ''
  )
  const [showTotals, setShowTotals] = useState(
    chart?.params?.show_totals || false
  )
  const [valueColumn, setValueColumn] = useState(
    chart?.params?.slice_column_id || chart?.params?.value_column_id || ''
  )
  const [aggregationMethod, setAggregationMethod] = useState(
    chart?.params?.slice_aggregation ||
      chart?.params?.aggregation_method ||
      'sum'
  )
  const [kpiValueColumn, setKpiValueColumn] = useState(
    chart?.params?.value_column_id || ''
  )
  const [kpiAggregation, setKpiAggregation] = useState(
    chart?.params?.aggregation_method || 'sum'
  )
  const [kpiAnalytic, setKpiAnalytic] = useState(
    chart?.params?.has_no_analytics ? 'none' : (chart?.params?.analytic_id || '')
  )
  const [textContent, setTextContent] = useState(chart?.params?.text_content || '')
  const [textRepeatOnEveryPage, setTextRepeatOnEveryPage] = useState(
    chart?.params?.repeat_on_every_page || false
  )


  // Header chart state
  const [savedHeaderSubtitle, setSavedHeaderSubtitle] = useState(chart?.params?.subtitle || '')
  const [savedHeaderBackgroundColor, setSavedHeaderBackgroundColor] = useState(chart?.params?.background_color || '')
  const [savedHeaderShowLogo, setSavedHeaderShowLogo] = useState(chart?.params?.show_logo !== false)
  const [savedHeaderTitleColor, setSavedHeaderTitleColor] = useState(chart?.params?.title_color || '')
  const [savedHeaderSubtitleColor, setSavedHeaderSubtitleColor] = useState(chart?.params?.subtitle_color || '')
  const [savedHeaderFontSize, setSavedHeaderFontSize] = useState(chart?.params?.font_size || 'md')
  const [savedHeaderTextAlign, setSavedHeaderTextAlign] = useState(chart?.params?.text_align || 'left')
  const [savedHeaderShowDivider, setSavedHeaderShowDivider] = useState(chart?.params?.show_divider || false)
  const [savedHeaderLogoSize, setSavedHeaderLogoSize] = useState(chart?.params?.logo_size || 'md')
  const [savedHeaderShowDate, setSavedHeaderShowDate] = useState(chart?.params?.show_date || false)
  const [savedHeaderRepeatOnEveryPage, setSavedHeaderRepeatOnEveryPage] = useState(
    chart?.params?.repeat_on_every_page || false
  )
  const [headerSubtitle, setHeaderSubtitle] = useState(chart?.params?.subtitle || '')
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState(chart?.params?.background_color || '')
  const [headerShowLogo, setHeaderShowLogo] = useState(chart?.params?.show_logo !== false)
  const [headerTitleColor, setHeaderTitleColor] = useState(chart?.params?.title_color || '')
  const [headerSubtitleColor, setHeaderSubtitleColor] = useState(chart?.params?.subtitle_color || '')
  const [headerFontSize, setHeaderFontSize] = useState(chart?.params?.font_size || 'md')
  const [headerTextAlign, setHeaderTextAlign] = useState(chart?.params?.text_align || 'left')
  const [headerShowDivider, setHeaderShowDivider] = useState(chart?.params?.show_divider || false)
  const [headerLogoSize, setHeaderLogoSize] = useState(chart?.params?.logo_size || 'md')
  const [headerShowDate, setHeaderShowDate] = useState(chart?.params?.show_date || false)
  const [headerRepeatOnEveryPage, setHeaderRepeatOnEveryPage] = useState(
    chart?.params?.repeat_on_every_page || false
  )

  // Default header title/subtitle to dashboard name/description for new charts
  useEffect(() => {
    if (!chart?.id && chartType === 'header' && dashboard) {
      if (!title && dashboard.name) setTitle(dashboard.name)
      if (!headerSubtitle && dashboard.description) setHeaderSubtitle(dashboard.description)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.id, chartType, dashboard])

  // Pivot table column aggregations: "saved" state (used for queries) vs "draft" state (used in modal)
  const [savedColumnAggregations, setSavedColumnAggregations] = useState(
    chart?.params?.column_aggregations || [
      { pivot_column_id: '', aggregation_method: 'count', position: 0, decimal_places: 0 }
    ]
  )
  const [draftColumnAggregations, setDraftColumnAggregations] = useState(
    chart?.params?.column_aggregations || [
      { pivot_column_id: '', aggregation_method: 'count', position: 0, decimal_places: 0 }
    ]
  )

  // Additional pivot row group-bys: array of { column_id, date_grouping, position }
  const initialAdditionalGroupBys = (chart?.params?.additional_pivot_row_columns || []).map((c, i) => ({
    column_id: c.id,
    date_grouping: c.date_grouping || 'day',
    segment_display: c.segment_display || SEGMENT_DISPLAY_DEFAULT,
    date_format: c.date_format || DATE_FORMAT_DEFAULT,
    position: i
  }))
  const [savedAdditionalGroupBys, setSavedAdditionalGroupBys] = useState(initialAdditionalGroupBys)
  const [draftAdditionalGroupBys, setDraftAdditionalGroupBys] = useState(initialAdditionalGroupBys)

  // Pivot table inline-groups toggle: render additional row groupings as separate
  // columns (flat layout) instead of nested expandable rows.
  const [savedPivotInlineGroups, setSavedPivotInlineGroups] = useState(!!chart?.params?.pivot_inline_groups)
  const [pivotInlineGroups, setPivotInlineGroups] = useState(!!chart?.params?.pivot_inline_groups)

  // Pivot table Total row toggle: append a grand-total row below the grouped rows.
  const [savedPivotShowTotals, setSavedPivotShowTotals] = useState(!!chart?.params?.pivot_show_totals)
  const [pivotShowTotals, setPivotShowTotals] = useState(!!chart?.params?.pivot_show_totals)

  // Data table columns: "saved" state (used for queries) vs "draft" state (used in modal)
  const [savedDataTableColumns, setSavedDataTableColumns] = useState(
    chart?.params?.columns || []
  )
  const [draftDataTableColumns, setDraftDataTableColumns] = useState(
    chart?.params?.columns || []
  )

  const [validationErrors, setValidationErrors] = useState({})
  const [hasSubmitted, setHasSubmitted] = useState(false)

  // Sync chart prop changes to local state after updates (JSON.stringify for deep comparison)
  // Sync all saved (rendered) state when the chart prop changes externally —
  // e.g. after an AI assistant update that bypasses handleMutationSuccess.
  const chartParamsJson = JSON.stringify(chart?.params || null)
  const chartUpdatedAt = chart?.updated_at
  useEffect(() => {
    if (!chart?.params) return
    const p = chart.params
    setSavedChartType(chart.chart_type || 'line')
    setSavedTitle(chart.title || '')
    setSavedGroupBy(p.group_by_column_id || p.pivot_row_column_id || '')
    setSavedGroupBy2(p.group_by2_column_id || p.pivot_column_column_id || '')
    setSavedXAxisColumn(p.x_axis_column_id || '')
    setSavedXAxisDateGrouping(p.x_axis_date_grouping || 'day')
    setSavedPivotRowDateGrouping(p.pivot_row_date_grouping || 'day')
    setSavedPivotRowSegmentDisplay(p.pivot_row_segment_display || SEGMENT_DISPLAY_DEFAULT)
    setSavedPivotRowDateFormat(p.pivot_row_date_format || DATE_FORMAT_DEFAULT)
    setSavedYAxisColumn(p.y_axis_column_id || '')
    setSavedYAxisAggregation(p.y_axis_aggregation || 'sum')
    setSavedYAxisLabel(p.y_axis_label || '')
    setSavedYAxisGroupBy(p.y_axis_group_by_column_id || '')
    setSavedDisplayLimit(p.display_limit ?? '')
    setSavedShowTotals(p.show_totals || false)
    setSavedValueColumn(p.slice_column_id || p.value_column_id || '')
    setSavedAggregationMethod(p.slice_aggregation || p.aggregation_method || 'sum')
    setSavedKpiValueColumn(p.value_column_id || '')
    setSavedKpiAggregation(p.aggregation_method || 'sum')
    setSavedKpiAnalytic(p.has_no_analytics ? 'none' : (p.analytic_id || ''))
    setSavedTextContent(p.text_content || '')
    setSavedTextRepeatOnEveryPage(p.repeat_on_every_page || false)
    setSavedHeaderSubtitle(p.subtitle || '')
    setSavedHeaderBackgroundColor(p.background_color || '')
    setSavedHeaderShowLogo(p.show_logo !== false)
    setSavedHeaderTitleColor(p.title_color || '')
    setSavedHeaderSubtitleColor(p.subtitle_color || '')
    setSavedHeaderFontSize(p.font_size || 'md')
    setSavedHeaderTextAlign(p.text_align || 'left')
    setSavedHeaderShowDivider(p.show_divider || false)
    setSavedHeaderLogoSize(p.logo_size || 'md')
    setSavedHeaderShowDate(p.show_date || false)
    setSavedHeaderRepeatOnEveryPage(p.repeat_on_every_page || false)
    setSavedBrandingType(chart.branding_type || '')
    setSavedBrandingColor(chart.branding_color || '')
  }, [chartParamsJson, chartUpdatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  const chartColumnAggsJson = JSON.stringify(
    chart?.params?.column_aggregations || null
  )
  useEffect(() => {
    if (chart?.params?.column_aggregations) {
      setSavedColumnAggregations(chart.params.column_aggregations)
      setDraftColumnAggregations(chart.params.column_aggregations)
    }
  }, [chartColumnAggsJson]) // eslint-disable-line react-hooks/exhaustive-deps

  const chartDataTableColumnsJson = JSON.stringify(chart?.params?.columns || null)
  useEffect(() => {
    if (chart?.params?.columns) {
      setSavedDataTableColumns(chart.params.columns)
      setDraftDataTableColumns(chart.params.columns)
    }
  }, [chartDataTableColumnsJson]) // eslint-disable-line react-hooks/exhaustive-deps

  const chartAdditionalGroupBysJson = JSON.stringify(
    chart?.params?.additional_pivot_row_columns || null
  )
  useEffect(() => {
    if (chart?.params?.additional_pivot_row_columns) {
      const next = chart.params.additional_pivot_row_columns.map((c, i) => ({
        column_id: c.id,
        date_grouping: c.date_grouping || 'day',
        segment_display: c.segment_display || SEGMENT_DISPLAY_DEFAULT,
        date_format: c.date_format || DATE_FORMAT_DEFAULT,
        position: i
      }))
      setSavedAdditionalGroupBys(next)
      setDraftAdditionalGroupBys(next)
    }
  }, [chartAdditionalGroupBysJson]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSavedPivotInlineGroups(!!chart?.params?.pivot_inline_groups)
    setPivotInlineGroups(!!chart?.params?.pivot_inline_groups)
  }, [chart?.params?.pivot_inline_groups])

  useEffect(() => {
    setSavedPivotShowTotals(!!chart?.params?.pivot_show_totals)
    setPivotShowTotals(!!chart?.params?.pivot_show_totals)
  }, [chart?.params?.pivot_show_totals])

  // Filter excluded columns from dropdowns
  const getFilteredColumns = () => {
    const excludedColumns = [
      'attributes',
      'overall',
      'transaction status',
      'report analytics count',
      'thirdline id'
    ]
    return (
      columns?.filter(
        (c) => !excludedColumns.includes(c.name?.toLowerCase())
      ) || []
    )
  }

  const getValueColumns = () => measureColumns(columns)

  const getGroupingColumns = () => {
    return getFilteredColumns()
  }

  const filteredColumns = getFilteredColumns()

  const getAggregationOptions = (columnId) =>
    aggregationOptions(columns?.find((c) => c.id === columnId))

  useEffect(() => {
    if (isColumnsSuccess && !groupBy) {
      setGroupBy(filteredColumns[0]?.id || '')
    }
    if (
      isColumnsSuccess &&
      chartType === 'pivot-table' &&
      draftColumnAggregations.length === 0
    ) {
      const firstValueColumn = defaultMeasureColumn(columns)
      if (firstValueColumn) {
        const defaultAggregation =
          getAggregationOptions(firstValueColumn.id)[0] || 'count'
        setDraftColumnAggregations([
          {
            pivot_column_id: firstValueColumn.id,
            aggregation_method: defaultAggregation,
            position: 0,
            decimal_places: 0
          }
        ])
        if (!chart?.id) {
          setSavedColumnAggregations([
            {
              pivot_column_id: firstValueColumn.id,
              aggregation_method: defaultAggregation,
              position: 0,
              decimal_places: 0
            }
          ])
        }
      }
    }
    if (isColumnsSuccess && chartType === 'scatter-plot' && !xAxisColumn) {
      setXAxisColumn(filteredColumns[0]?.id || '')
    }
    if (isColumnsSuccess && chartType === 'scatter-plot' && !yAxisColumn) {
      setYAxisColumn(filteredColumns[1]?.id || '')
    }
    if (
      isColumnsSuccess &&
      chartType === 'kpi' &&
      !kpiValueColumn &&
      getValueColumns().length > 0
    ) {
      setKpiValueColumn(defaultMeasureColumn(columns)?.id || '')
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isColumnsSuccess,
    filteredColumns,
    groupBy,
    chartType,
    draftColumnAggregations,
    xAxisColumn,
    yAxisColumn,
    valueColumn,
    kpiValueColumn,
    columns,
    chart
  ])

  // Validate and auto-correct aggregation methods when columns change
  useEffect(() => {
    if (isColumnsSuccess && yAxisColumn) {
      const availableOptions = getAggregationOptions(yAxisColumn)
      if (!availableOptions.includes(yAxisAggregation)) {
        setYAxisAggregation(availableOptions[0])
      }
    }
    if (isColumnsSuccess && valueColumn && chartType === 'pie-donut') {
      const availableOptions = getAggregationOptions(valueColumn)
      if (!availableOptions.includes(aggregationMethod)) {
        setAggregationMethod(availableOptions[0])
      }
    }
    if (isColumnsSuccess && kpiValueColumn && chartType === 'kpi') {
      const availableOptions = getAggregationOptions(kpiValueColumn)
      if (!availableOptions.includes(kpiAggregation)) {
        setKpiAggregation(availableOptions[0])
      }
    }
    if (
      isColumnsSuccess &&
      chartType === 'pivot-table' &&
      draftColumnAggregations.length > 0
    ) {
      let needsUpdate = false
      const updatedAggregations = draftColumnAggregations.map((agg) => {
        if (agg.pivot_column_id) {
          const availableOptions = getAggregationOptions(agg.pivot_column_id)
          if (!availableOptions.includes(agg.aggregation_method)) {
            needsUpdate = true
            return { ...agg, aggregation_method: availableOptions[0] }
          }
        }
        return agg
      })
      if (needsUpdate) {
        setDraftColumnAggregations(updatedAggregations)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isColumnsSuccess,
    yAxisColumn,
    valueColumn,
    aggregationMethod,
    kpiValueColumn,
    kpiAggregation,
    draftColumnAggregations,
    chartType,
    columns
  ])

  // Revalidate form on field changes after user submits
  useEffect(() => {
    if (
      hasSubmitted &&
      (title ||
        xAxisColumn ||
        yAxisColumn ||
        groupBy ||
        groupBy2 ||
        valueColumn ||
        kpiValueColumn ||
        xAxisDateGrouping ||
        yAxisAggregation ||
        yAxisLabel ||
        kpiAggregation ||
        textContent)
    ) {
      const currentErrors = { ...validationErrors }

      if (title?.trim()) delete currentErrors.title
      if (xAxisColumn) delete currentErrors.xAxisColumn
      if (yAxisColumn) delete currentErrors.yAxisColumn
      if (groupBy) delete currentErrors.groupBy
      if (groupBy2) delete currentErrors.groupBy2
      if (valueColumn) delete currentErrors.valueColumn
      if (kpiValueColumn) delete currentErrors.kpiValueColumn
      if (xAxisDateGrouping) delete currentErrors.xAxisDateGrouping
      if (yAxisAggregation) delete currentErrors.yAxisAggregation
      if (yAxisLabel) delete currentErrors.yAxisLabel
      if (kpiAggregation) delete currentErrors.kpiAggregation
      if (draftColumnAggregations.length > 0)
        delete currentErrors.columnAggregations

      setValidationErrors(currentErrors)

      const timer = setTimeout(() => {
        validateForm()
      }, 100)

      return () => clearTimeout(timer)
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    xAxisColumn,
    yAxisColumn,
    yAxisGroupBy,
    groupBy,
    groupBy2,
    valueColumn,
    kpiValueColumn,
    xAxisDateGrouping,
    pivotRowDateGrouping,
    yAxisAggregation,
    yAxisLabel,
    kpiAggregation,
    draftColumnAggregations,
    chartType,
    hasSubmitted,
    textContent
  ])

  // Charts filter on the same params as their View's grid, through the same builder.
  const token = useContext(AuthContext)
  const selectedDataSource = dataSources.find((source) => source.id === draftDataSourceId)
  const viewDashboardId = params?.dashboardId || selectedDataSource?.dashboardId
  const { data: customColumns = [], isLoading: isCustomColumnsLoading } = useQuery(
    ['customColumns', viewDashboardId],
    () => customColumnService.getCustomColumns(token, viewDashboardId),
    { enabled: !!viewDashboardId && !!token }
  )

  const isDataChart = !['header', 'text', 'page-break'].includes(chartType)
  const isSampleSource = !!selectedDataSource?.isSample
  const chartFilterColumns = useMemo(
    () => buildFilterColumns({
      columns, customColumns, analytics: analytics?.data || [], municipality, sectionId: resolvedSectionId
    }),
    [columns, customColumns, analytics, municipality, resolvedSectionId]
  )
  // Readers see that a chart narrows its View, and by what (the badge's tooltip).
  const savedChartFilterRequest = requestFilterTree(parseFilterTree(savedChartFilterTree))
  const savedChartFilterCount = countFilters(savedChartFilterRequest)
  const draftChartFilterCount = countFilters(requestFilterTree(chartFilterDraft))
  // Most charts have no filters of their own, so the section stays hidden unless this one does.
  const [showChartFilters, setShowChartFilters] = useState(false)
  useEffect(() => {
    if (showModal) setShowChartFilters(savedChartFilterCount > 0)
  }, [showModal, savedChartFilterCount])
  const chartFilterColumnFor = useCallback(
    (leaf) => chartFilterColumns.find(({ column }) => column.accessor_key === leaf.column)?.column,
    [chartFilterColumns]
  )
  const savedChartFilterDescription = useMemo(
    () => describeTree(parseFilterTree(savedChartFilterTree), chartFilterColumnFor),
    [savedChartFilterTree, chartFilterColumnFor]
  )
  // Shown under the chart, and on its image downloads, when the chart is set to show it.
  const filterCaption =
    savedShowFilterCaption && savedChartFilterCount > 0 && savedChartFilterDescription
      ? `Note: Filtered to ${savedChartFilterDescription}`
      : null
  const savedChartFilterOutline = useMemo(
    () => outlineTree(parseFilterTree(savedChartFilterTree), chartFilterColumnFor),
    [savedChartFilterTree, chartFilterColumnFor]
  )

  // What a save stores: the edited tree once it filters on something, else nothing. Without an
  // open settings draft (a branding save) the saved tree stands.
  const chartFilterTreeToSave = () => {
    if (chartFilterDraft === null) return savedChartFilterTree || ''
    return !isSampleSource && requestFilterTree(chartFilterDraft) ? JSON.stringify(chartFilterDraft) : ''
  }

  const buildBaseTransactionParams = useMemo(() => ({
    chart_id: chart?.id,
    page: params.page,
    perPage: params.perPage,
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
    include_ordering: false,
    ...transactionFilterParams(viewFilters(params), { columns, customColumns })
  }), [params, chart?.id, columns, customColumns])

  const txParams = useMemo(() => {
    const baseParams = prepParams(buildBaseTransactionParams)
    const formData = {
      groupBy: savedGroupBy,
      groupBy2: savedGroupBy2,
      xAxisColumn: savedXAxisColumn,
      xAxisDateGrouping: savedXAxisDateGrouping,
      pivotRowDateGrouping: savedPivotRowDateGrouping,
      pivotRowSegmentDisplay: savedPivotRowSegmentDisplay,
      pivotRowDateFormat: savedPivotRowDateFormat,
      yAxisColumn: savedYAxisColumn,
      yAxisAggregation: savedYAxisAggregation,
      yAxisGroupBy: savedYAxisGroupBy,
      displayLimit: savedDisplayLimit,
      valueColumn: savedValueColumn,
      aggregationMethod: savedAggregationMethod,
      kpiValueColumn: savedKpiValueColumn,
      kpiAggregation: savedKpiAggregation,
      kpiAnalytic: savedKpiAnalytic,
      savedPivotShowTotals,
      savedColumnAggregations,
      savedDataTableColumns
    }
    const params = buildQueryParams(
      savedChartType,
      baseParams,
      formData,
      columns
    )
    // Adapter-supplied params that must reach every data request for this chart, the pivot and
    // data-table pagination/sort calls included (see REPORT_CHART_ADAPTER.dataRequestParams).
    return pickPresentParams({ ...params, ...(adapter?.dataRequestParams?.(resolvedContainerId) || {}) })
  }, [
    adapter,
    resolvedContainerId,
    buildBaseTransactionParams,
    savedChartType,
    savedGroupBy,
    savedGroupBy2,
    savedXAxisColumn,
    savedXAxisDateGrouping,
    savedPivotRowDateGrouping,
    savedPivotRowSegmentDisplay,
    savedPivotRowDateFormat,
    savedYAxisColumn,
    savedYAxisAggregation,
    savedYAxisGroupBy,
    savedDisplayLimit,
    savedValueColumn,
    savedAggregationMethod,
    savedKpiValueColumn,
    savedKpiAggregation,
    savedKpiAnalytic,
    savedPivotShowTotals,
    savedColumnAggregations,
    savedDataTableColumns,
    columns
  ])

  // A custom-column filter can't be built until the columns arrive, so the first fetch would
  // otherwise go out unfiltered and be replaced a moment later.
  const isQueryEnabled = !isCustomColumnsLoading && isChartQueryEnabled(
    savedChartType,
    {
      xAxisColumn: savedXAxisColumn,
      yAxisColumn: savedYAxisColumn,
      groupBy: savedGroupBy,
      savedColumnAggregations,
      kpiValueColumn: savedKpiValueColumn,
      savedDataTableColumns,
      textContent: savedTextContent
    },
    !!chart?.id
  )

  const {
    data: countsRaw,
    isSuccess: isCountsSuccess,
    isError: isCountsError,
    isFetching: isCountsFetching,
    refetch: refetchCounts
  } = useChartData({
    containerId: resolvedContainerId,
    chartId: chart?.id,
    params: txParams,
    options: {
      // Nothing to fetch while the chart is unbound — the report chart-data endpoint answers
      // "not bound to a dataset", which would surface as a load failure rather than the
      // reconnect prompt renderChart shows for this case.
      enabled: isQueryEnabled && !dataSourceMissing,
      refetchOnWindowFocus: false
    }
  })

  // A chart that fetches (i.e. not header/text/page-break) is "loading" whenever its
  // query is enabled and hasn't produced data or an error yet — which covers both the
  // first fetch and a refetch triggered by a filter change.
  const isCountsLoading = isQueryEnabled && !dataSourceMissing && !isCountsSuccess && !isCountsError

  // A Hash result may carry the grain warnings BaseChart recorded (see Charts::Grain); they are
  // split off here so nothing downstream reads them as a data series.
  const grainWarnings = countsRaw?.grain_warnings || []
  const counts = useMemo(() => {
    if (countsRaw && !Array.isArray(countsRaw) && 'grain_warnings' in countsRaw) {
      const { grain_warnings: _ignored, ...rest } = countsRaw
      return rest
    }
    if (countsRaw) return countsRaw
    if (savedChartType === 'scatter-plot') return []
    if (savedChartType === 'data-table') return { data: [], columns: [], total_count: 0, has_more: false }
    return {}
  }, [countsRaw, savedChartType])

  const handleMutationSuccess = async () => {
    // Commit draft state to saved state
    setSavedDataSourceId(draftDataSourceId)
    if (isDataChart) {
      setSavedChartFilterTree(chartFilterTreeToSave() || null)
      setSavedShowFilterCaption(draftShowFilterCaption)
    }
    setSavedChartType(chartType)
    setSavedGroupBy(groupBy)
    setSavedGroupBy2(groupBy2)
    setSavedTitle(title)
    setSavedXAxisColumn(xAxisColumn)
    setSavedXAxisDateGrouping(xAxisDateGrouping)
    setSavedPivotRowDateGrouping(pivotRowDateGrouping)
    setSavedPivotRowSegmentDisplay(pivotRowSegmentDisplay)
    setSavedPivotRowDateFormat(pivotRowDateFormat)
    setSavedYAxisColumn(yAxisColumn)
    setSavedYAxisAggregation(yAxisAggregation)
    setSavedYAxisLabel(yAxisLabel)
    setSavedYAxisGroupBy(yAxisGroupBy)
    setSavedDisplayLimit(displayLimit)
    setSavedShowTotals(showTotals)
    setSavedValueColumn(valueColumn)
    setSavedAggregationMethod(aggregationMethod)
    setSavedKpiValueColumn(kpiValueColumn)
    setSavedKpiAggregation(kpiAggregation)
    setSavedKpiAnalytic(kpiAnalytic)
    setSavedDataTableColumns(draftDataTableColumns)
    setSavedAdditionalGroupBys(draftAdditionalGroupBys)
    setSavedPivotInlineGroups(pivotInlineGroups)
    setSavedPivotShowTotals(pivotShowTotals)
    setSavedTextContent(textContent)
    setSavedHeaderSubtitle(headerSubtitle)
    setSavedHeaderBackgroundColor(headerBackgroundColor)
    setSavedHeaderShowLogo(headerShowLogo)
    setSavedHeaderTitleColor(headerTitleColor)
    setSavedHeaderSubtitleColor(headerSubtitleColor)
    setSavedHeaderFontSize(headerFontSize)
    setSavedHeaderTextAlign(headerTextAlign)
    setSavedHeaderShowDivider(headerShowDivider)
    setSavedHeaderLogoSize(headerLogoSize)
    setSavedHeaderShowDate(headerShowDate)
    setSavedHeaderRepeatOnEveryPage(headerRepeatOnEveryPage)
    setSavedTextRepeatOnEveryPage(textRepeatOnEveryPage)

    setShowModal(false)
    setChartFilterDraft(null)
    setShowDeleteConfirm(false)
    onSaved?.()

    adapter.invalidate(queryClient, resolvedContainerId)
  }

  const createMutation = useCreateChart({
    containerId: resolvedContainerId,
    options: {
      onSuccess: handleMutationSuccess
    }
  })

  const updateMutation = useUpdateChart({
    containerId: resolvedContainerId,
    options: {
      onSuccess: handleMutationSuccess
    }
  })

  const destroyMutation = useDestroyChart({
    containerId: resolvedContainerId,
    chartId: chart?.id,
    options: {
      onSuccess: handleMutationSuccess
    }
  })

  const exportParams = {
    name: exportName,
    source: savedChartType === 'data-table' ? 'dataTable' : 'pivotTable',
    status: 'queued',
    params: {
      ...txParams,
      dashboard_id: dashboardId,
      chart_id: chart?.id
    }
  }

  const { mutate: createExport, isSuccess: isExportSuccess } = useCreateExport({
    params: exportParams
  })

  useEffect(() => {
    if (isExportSuccess) {
      setShowExportModal(false)
      toast.success('File will be available in Exports shortly')
    }
  }, [isExportSuccess]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenExportModal = () => {
    const defaultName = savedChartType === 'data-table' ? 'Data Table' : 'Pivot Table'
    setExportName(savedTitle || defaultName)
    setShowExportModal(true)
  }

  const handlePivotTableExport = () => {
    createExport()
  }

  const validateForm = () => {
    const errors = validateChartForm(chartType, {
      title,
      xAxisColumn,
      yAxisColumn,
      groupBy,
      kpiValueColumn,
      draftColumnAggregations,
      draftDataTableColumns
    })

    // Reports only (dashboards pass no dataSources): saving a data-bound chart with nothing
    // selected would fail the API's report_dataset_id presence check, so catch it in the modal
    // where the picker is.
    if (canPickDataSource && chartTypeNeedsDataSource(chartType, textContent) && !draftDataSourceId) {
      errors.dataSource = 'Data source is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    setHasSubmitted(true)
    const isValid = validateForm()
    if (!isValid) {
      return
    }

    const params = buildChartParams(chartType, {
      groupBy,
      xAxisColumn,
      xAxisDateGrouping,
      pivotRowDateGrouping,
      pivotRowSegmentDisplay,
      pivotRowDateFormat,
      yAxisColumn,
      yAxisAggregation,
      yAxisLabel,
      yAxisGroupBy,
      displayLimit,
      showTotals,
      valueColumn,
      aggregationMethod,
      kpiValueColumn,
      kpiAggregation,
      kpiAnalytic,
      draftColumnAggregations,
      draftAdditionalGroupBys,
      pivotInlineGroups,
      pivotShowTotals,
      draftDataTableColumns,
      textContent,
      headerSubtitle,
      headerBackgroundColor,
      headerShowLogo,
      headerTitleColor,
      headerSubtitleColor,
      headerFontSize,
      headerTextAlign,
      headerShowDivider,
      headerLogoSize,
      headerShowDate,
      headerRepeatOnEveryPage,
      textRepeatOnEveryPage
    })

    // Sanitize params to remove any parameters not relevant to this chart type
    const sanitizedParams = sanitizeChartParams(chartType, params)

    const payload = adapter.buildPayload({
      chartId: chart?.id,
      containerId: resolvedContainerId,
      chartType,
      title,
      params: sanitizedParams,
      gridRowId,
      gridColumnPosition,
      dataSourceId: draftDataSourceId,
      extra: isDataChart
        ? { ...extraPayload, filter_tree: chartFilterTreeToSave(), show_filter_caption: draftShowFilterCaption }
        : extraPayload
    })

    if (chart?.id) {
      await updateMutation.mutate(payload)
    } else {
      await createMutation.mutate(payload)
    }
  }

  const handleOpenDelete = () => {
    setShowModal(false)
    setDeleteFromModal(true)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    setShowModal(false)
    setShowDeleteConfirm(false)
    setDeleteFromModal(false)
    await destroyMutation.mutate()
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
    setDeleteFromModal(false)
    if (deleteFromModal) setShowModal(true)
  }

  const handleBrandingSave = async (brandingData) => {
    if (!chart?.id) return

    const payload = adapter.buildPayload({
      chartId: chart.id,
      containerId: resolvedContainerId,
      chartType: savedChartType,
      title: savedTitle,
      params: chart.params,
      gridRowId,
      gridColumnPosition,
      dataSourceId: savedDataSourceId,
      extra: {
        ...extraPayload,
        branding_type: brandingData.branding_type,
        branding_color: brandingData.branding_color
      }
    })

    await updateMutation.mutate(payload)

    // Update saved state
    setSavedBrandingType(brandingData.branding_type || '')
    setSavedBrandingColor(brandingData.branding_color || '')

    setShowBrandingModal(false)
  }

  const handleOpenModal = () => {
    // A page break has no configuration, so there is nothing to edit.
    if (savedChartType === 'page-break') return
    // A computed block has no editable text; the server refuses a body update anyway.
    if (isComputed) return
    // Sync saved state back to draft state when opening modal
    setDraftDataSourceId(savedDataSourceId)
    setChartFilterDraft(startingTree(savedChartFilterTree))
    setDraftShowFilterCaption(savedShowFilterCaption)
    setChartType(savedChartType)
    setGroupBy(savedGroupBy)
    setGroupBy2(savedGroupBy2)
    setTitle(savedTitle)
    setXAxisColumn(savedXAxisColumn)
    setXAxisDateGrouping(savedXAxisDateGrouping)
    setPivotRowDateGrouping(savedPivotRowDateGrouping)
    setPivotRowSegmentDisplay(savedPivotRowSegmentDisplay)
    setPivotRowDateFormat(savedPivotRowDateFormat)
    setYAxisColumn(savedYAxisColumn)
    setYAxisAggregation(savedYAxisAggregation)
    setYAxisLabel(savedYAxisLabel)
    setYAxisGroupBy(savedYAxisGroupBy)
    setDisplayLimit(savedDisplayLimit)
    setShowTotals(savedShowTotals)
    setValueColumn(savedValueColumn)
    setAggregationMethod(savedAggregationMethod)
    setKpiValueColumn(savedKpiValueColumn)
    setKpiAggregation(savedKpiAggregation)
    setKpiAnalytic(savedKpiAnalytic)
    setDraftDataTableColumns(savedDataTableColumns)
    setDraftAdditionalGroupBys(savedAdditionalGroupBys)
    setPivotInlineGroups(savedPivotInlineGroups)
    setPivotShowTotals(savedPivotShowTotals)
    setTextContent(savedTextContent)
    setHeaderSubtitle(savedHeaderSubtitle)
    setHeaderBackgroundColor(savedHeaderBackgroundColor)
    setHeaderShowLogo(savedHeaderShowLogo)
    setHeaderTitleColor(savedHeaderTitleColor)
    setHeaderSubtitleColor(savedHeaderSubtitleColor)
    setHeaderFontSize(savedHeaderFontSize)
    setHeaderTextAlign(savedHeaderTextAlign)
    setHeaderShowDivider(savedHeaderShowDivider)
    setHeaderLogoSize(savedHeaderLogoSize)
    setHeaderShowDate(savedHeaderShowDate)
    setHeaderRepeatOnEveryPage(savedHeaderRepeatOnEveryPage)
    setTextRepeatOnEveryPage(savedTextRepeatOnEveryPage)
    setShowModal(true)
  }

  const handleCancelModal = () => {
    // Reset draft state back to saved state on cancel
    setDraftDataSourceId(savedDataSourceId)
    setChartFilterDraft(null)
    setChartType(savedChartType)
    setGroupBy(savedGroupBy)
    setGroupBy2(savedGroupBy2)
    setTitle(savedTitle)
    setXAxisColumn(savedXAxisColumn)
    setXAxisDateGrouping(savedXAxisDateGrouping)
    setPivotRowDateGrouping(savedPivotRowDateGrouping)
    setPivotRowSegmentDisplay(savedPivotRowSegmentDisplay)
    setPivotRowDateFormat(savedPivotRowDateFormat)
    setYAxisColumn(savedYAxisColumn)
    setYAxisAggregation(savedYAxisAggregation)
    setYAxisLabel(savedYAxisLabel)
    setYAxisGroupBy(savedYAxisGroupBy)
    setDisplayLimit(savedDisplayLimit)
    setShowTotals(savedShowTotals)
    setValueColumn(savedValueColumn)
    setAggregationMethod(savedAggregationMethod)
    setKpiValueColumn(savedKpiValueColumn)
    setKpiAggregation(savedKpiAggregation)
    setKpiAnalytic(savedKpiAnalytic)
    setDraftDataTableColumns(savedDataTableColumns)
    setDraftAdditionalGroupBys(savedAdditionalGroupBys)
    setPivotInlineGroups(savedPivotInlineGroups)
    setPivotShowTotals(savedPivotShowTotals)
    setTextContent(savedTextContent)
    setHeaderSubtitle(savedHeaderSubtitle)
    setHeaderBackgroundColor(savedHeaderBackgroundColor)
    setHeaderShowLogo(savedHeaderShowLogo)
    setHeaderTitleColor(savedHeaderTitleColor)
    setHeaderSubtitleColor(savedHeaderSubtitleColor)
    setHeaderFontSize(savedHeaderFontSize)
    setHeaderTextAlign(savedHeaderTextAlign)
    setHeaderShowDivider(savedHeaderShowDivider)
    setHeaderLogoSize(savedHeaderLogoSize)
    setHeaderShowDate(savedHeaderShowDate)
    setHeaderRepeatOnEveryPage(savedHeaderRepeatOnEveryPage)
    setTextRepeatOnEveryPage(savedTextRepeatOnEveryPage)
    setShowModal(false)
    setHasSubmitted(false)
    setValidationErrors({})
    if (!chart?.id && onCancel) {
      onCancel()
    }
  }

  const handleChartTypeChange = (newChartType) => {
    // Only allow chart type changes for new charts
    if (!chart?.id) {
      setChartType(newChartType)
      // Clear validation errors when chart type changes
      setValidationErrors({})
      // Also run validation on current form state only if user has attempted to submit
      setTimeout(() => {
        if (hasSubmitted && title) validateForm()
      }, 100)
    }
  }

  const handleExportChart = (format) => {
    if (chartRef.current && chartRef.current.chart) {
      const filename = savedTitle || 'chart'
      // A downloaded image carries the chart's filter caption, as a PDF export does.
      const chartOptions = filterCaption ? { caption: { text: filterCaption } } : undefined

      if (format === 'svg') {
        // SVG export using getSVG
        chartRef.current.chart.exportChartLocal({
          type: 'image/svg+xml',
          filename: filename
        }, chartOptions)
      } else {
        // PNG and JPEG export using canvas
        chartRef.current.chart.exportChartLocal({
          type: `image/${format}`,
          filename: filename
        }, chartOptions)
      }
    }
  }

  const chartData = useMemo(() => {
    if (!isCountsSuccess) {
      return savedChartType === 'kpi'
        ? { value: 0 }
        : savedChartType === 'pivot-table'
        ? { data: {}, total_count: 0, has_more: false } // Return empty object for pivot table with pagination metadata
        : savedChartType === 'data-table'
        ? { data: [], columns: [], total_count: 0, has_more: false }
        : savedChartType === 'scatter-plot'
        ? []
        : { categories: [], series: [], entries: [] }
    }
    if (savedChartType === 'data-table') {
      if (counts && typeof counts === 'object' && 'data' in counts) {
        return counts
      }
      return { data: [], columns: [], total_count: 0, has_more: false }
    }
    if (savedChartType === 'pivot-table') {
      // For pivot table, expect data with pagination metadata
      // Backend returns { data: {}, total_count: N, offset: N, limit: N, has_more: boolean }
      if (counts && typeof counts === 'object' && 'data' in counts) {
        return counts
      }
      // Fallback for old format (plain object)
      return {
        data: Array.isArray(counts) ? {} : counts,
        total_count: 0,
        has_more: false
      }
    }
    if (savedChartType === 'scatter-plot') return counts
    if (savedChartType === 'kpi') {
      // For KPI, we expect a single aggregated value
      // The backend should return a single key-value pair for KPIs
      const entries = Object.entries(counts || {})
      const totalValue = entries.reduce(
        (sum, [_, value]) => sum + (parseFloat(value) || 0),
        0
      )
      return { value: totalValue }
    }

    // Handle grouped data (from Y-axis grouping)
    if (counts?.has_grouping) {
      let categories = counts.categories || []

      // Format date labels for grouped data if chart supports it
      if (
        supportsDateFormatting(savedChartType) &&
        savedXAxisDateGrouping &&
        savedXAxisColumn &&
        columns?.find((c) => c.id === savedXAxisColumn)?.display_as === 'date'
      ) {
        categories = formatDateLabels(categories, savedXAxisDateGrouping)
      }

      // [x, y] pairs: bare numbers get renumbered from x=0 by an in-place update, and object
      // points past Highcharts' turboThreshold (1000) throw error #12.
      return {
        categories,
        series: (counts.series || []).map((s) => ({
          ...s,
          data: (s.data || []).map((y, x) => [x, y])
        })),
        entries: [],
        has_grouping: true
      }
    }

    // Original logic for non-grouped data
    const entries = Object.entries(counts)?.map(([k, v]) => ({
      key: k,
      value: v
    }))

    let categories = Object.keys(counts)

    // Format date labels for charts with date grouping if chart supports it
    if (
      supportsDateFormatting(savedChartType) &&
      savedXAxisDateGrouping &&
      savedXAxisColumn &&
      columns?.find((c) => c.id === savedXAxisColumn)?.display_as === 'date'
    ) {
      categories = formatDateLabels(categories, savedXAxisDateGrouping)
    }

    return {
      categories,
      // coerce strings into floats so the chart can display them; do it here
      // so the series reference is stable across re-renders (needed for React.memo
      // on chart components to short-circuit when nothing actually changed)
      series: Object.values(counts).map((d) => parseFloat(d)),
      entries
    }
  }, [
    counts,
    isCountsSuccess,
    savedChartType,
    savedXAxisDateGrouping,
    savedXAxisColumn,
    columns
  ])

  const hasGrouping = chartData.has_grouping

  const [drilldown, setDrilldown] = useState(null)
  const canDrillDown = !!adapter.drillDown && !!chart?.id && DRILLABLE_CHART_TYPES.includes(savedChartType)

  // Read through a ref so the chart components' memoized options stay stable across renders.
  // Drill-downs send the raw keys the API grouped by, not the formatted labels.
  const pointClickRef = useRef(null)
  pointClickRef.current = ({ x, index, seriesName }) => {
    if (savedChartType === 'pie-donut') {
      const key = pieSliceKey(chartData.entries[index]?.key)
      setDrilldown({ values: [key], labels: [key] })
    } else if (savedChartType === 'scatter-plot') {
      // Both axes are usually numbers, so each pill says which axis it is.
      const { x_key: xKey, y_key: yKey } = chartData[index] || {}
      const axisValue = (column, value) => {
        if (column?.display_as === 'date') return formatDateTimeValue(value)
        return Number.isNaN(Number(value)) ? String(value) : formatValueByColumn(value, column, null)
      }
      const axisLabel = (column, value) => `${getColumnDisplayName(column, municipality)}: ${axisValue(column, value)}`
      setDrilldown({ values: [xKey, yKey], labels: [axisLabel(xAxisCol, xKey), axisLabel(yAxisCol, yKey)] })
    } else if (hasGrouping) {
      const withSeries = seriesName && seriesName !== 'Total'
      const label = chartData.categories[x]
      setDrilldown({
        values: withSeries ? [counts.categories?.[x], seriesName] : [counts.categories?.[x]],
        labels: withSeries ? [label, seriesName] : [label]
      })
    } else {
      setDrilldown({ values: [chartData.entries[x]?.key], labels: [chartData.categories[x]] })
    }
  }
  const handlePointClick = useCallback((point) => pointClickRef.current(point), [])
  const handlePivotRowClick = useCallback((values, labels) => setDrilldown({ values, labels }), [])
  const handleDataTableRowClick = useCallback(
    (transactionId, thirdlineId) =>
      setDrilldown({ values: [transactionId], labels: thirdlineId ? [`ThirdLine ID ${thirdlineId}`] : [] }),
    []
  )
  const pointClick = canDrillDown ? handlePointClick : null

  // KPI cards are short; every other chart/table type renders at roughly Highcharts'
  // default height, so reserve that much while loading or erroring to avoid a jump.
  const loadingMinHeight = savedChartType === 'kpi' ? 140 : 300
  const chartNoun =
    savedChartType === 'kpi'
      ? 'KPI'
      : savedChartType === 'pivot-table' || savedChartType === 'data-table'
      ? 'table'
      : 'chart'

  // Use a stable key based on chart ID to prevent unnecessary re-mounts
  // HighchartsReact will efficiently update the chart when options change
  const chartKey = chart?.id
    ? `chart-${chart.id}`
    : `new-chart-${savedChartType}`

  // Compute the Y-axis title, defaulting to the aggregation type when empty
  const computedYAxisTitle =
    savedYAxisLabel || savedYAxisAggregation.toUpperCase()

  const yAxisCol = columns?.find((c) => c.id === savedYAxisColumn)
  const xAxisCol = columns?.find((c) => c.id === savedXAxisColumn)

  // Memoize tooltip formatter to prevent recreation on every render
  const tooltipFormatter = useMemo(
    () =>
      createTooltipFormatter({
        column: yAxisCol,
        aggregation: savedYAxisAggregation,
        isGrouped: hasGrouping
      }),
    [yAxisCol, savedYAxisAggregation, hasGrouping]
  )

  // Compute branding colors based on saved state
  const brandingColors = useMemo(() => {
    return getChartBrandingColors(
      municipalitySettings,
      savedBrandingType,
      savedBrandingColor
    )
  }, [municipalitySettings, savedBrandingType, savedBrandingColor])

  // Stable prop bundle for series-based charts. Recomputing on every render
  // hands HighchartsReact a fresh options object, which it interprets as a
  // change and applies via chart.update() — disruptive mid-animation.
  const commonProps = useMemo(
    () => ({
      categories: chartData.categories,
      series: chartData.series,
      title: null,
      hasGrouping
    }),
    [chartData, hasGrouping]
  )

  // Pie chart's tooltip needs raw point context (percentage, total) that
  // the shared formatter doesn't expose, so it gets its own stable formatter.
  const pieTooltipFormatter = useMemo(
    () =>
      function () {
        const formattedValue = formatValueByColumn(this.y, null, null)
        const percentage = formatValueByColumn(this.percentage, null, null)
        const total = formatValueByColumn(this.total, null, null)
        return `<b>${this.key}</b>: ${formattedValue} (${percentage}%) of ${total}`
      },
    []
  )

  // The picker stands in for the read-only data source line whenever the report offers a choice
  // and this chart type actually draws from one.
  const showDataSourcePicker = canPickDataSource && chartTypeNeedsDataSource(chartType, textContent)
  // Columns belong to a section, so re-pointing a chart at a data source from a different one
  // invalidates every column it has configured. Say so in the modal, not after the save.
  const savedDataSourceSectionId = dataSources.find((source) => source.id === savedDataSourceId)?.sectionId
  const draftDataSourceSectionId = dataSources.find((source) => source.id === draftDataSourceId)?.sectionId
  const dataSourceHelpText =
    draftDataSourceId &&
    savedDataSourceSectionId &&
    draftDataSourceSectionId &&
    draftDataSourceSectionId !== savedDataSourceSectionId
      ? 'This data source covers a different section, so the columns below need to be picked again.'
      : null

  const grainWarningNotice = grainWarnings.length > 0 && (
    <p className="text-warning mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
      {grainWarnings.join(' ')}
    </p>
  )

  const renderChart = () => {
    // The chart's linked data source (View) was removed from the report, so there's nothing to
    // fetch/render. Surface it plainly instead of a confusing empty/errored chart. (header carries
    // no data source, and text only once it quotes the data — see chartTypeNeedsDataSource.)
    if (dataSourceMissing) {
      return (
        <div className="text-center p-4 text-muted">
          <p className="mb-1 fw-semibold">Data source unavailable</p>
          <p className="small mb-0">
            {canPickDataSource
              ? 'The View this component was built from is no longer linked to this report. Edit the component and pick a data source to reconnect it.'
              : 'The View this component was built from is no longer linked to this report.'}
          </p>
        </div>
      )
    }
    if (savedChartType === 'text') {
      return (
        <>
          <TextChart
            key={chartKey}
            content={savedTextContent}
            params={params}
            tableColumns={columns}
            municipalitySettings={municipalitySettings}
            values={counts?.values}
          />
          {staticFiguresNotice && (
            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
              {staticFiguresNotice}
            </p>
          )}
          {groupingStaleNotice && (
            <p className="text-danger mb-0 mt-1" style={{ fontSize: '0.8rem' }}>
              {groupingStaleNotice}
            </p>
          )}
        </>
      )
    }
    // Header charts do not fetch data; render immediately when the chart is saved.
    if (savedChartType === 'header') {
      return (
        <HeaderChart
          key={chartKey}
          title={savedTitle}
          subtitle={savedHeaderSubtitle}
          backgroundColor={savedHeaderBackgroundColor}
          showLogo={savedHeaderShowLogo}
          titleColor={savedHeaderTitleColor}
          subtitleColor={savedHeaderSubtitleColor}
          fontSize={savedHeaderFontSize}
          textAlign={savedHeaderTextAlign}
          showDivider={savedHeaderShowDivider}
          logoSize={savedHeaderLogoSize}
          showDate={savedHeaderShowDate}
        />
      )
    }
    // Page breaks carry no data; render the visual marker immediately.
    if (savedChartType === 'page-break') {
      return <PageBreakChart key={chartKey} />
    }

    // The chart's data query failed — say so inside the card rather than rendering an
    // empty frame, and offer a retry that refetches just this chart.
    if (isCountsError) {
      return (
        <CardError
          title={`Couldn't load this ${chartNoun}`}
          minHeight={loadingMinHeight}
          onRetry={(e) => {
            e?.stopPropagation?.()
            refetchCounts()
          }}
          isRetrying={isCountsFetching}
        />
      )
    }

    if (isCountsLoading) return <CardLoading minHeight={loadingMinHeight} />

    if (!isCountsSuccess) return null

    if (savedChartType === 'line') {
      return (
        <LineChart
          key={chartKey}
          {...commonProps}
          onPointClick={pointClick}
          yAxisTitle={computedYAxisTitle}
          tooltipFormatter={tooltipFormatter}
          showTotals={savedShowTotals}
          colors={brandingColors}
          chartRef={chartRef}
        />
      )
    }
    if (savedChartType === 'vertical-bar') {
      return (
        <VerticalBarChart
          key={chartKey}
          {...commonProps}
          onPointClick={pointClick}
          yAxisTitle={computedYAxisTitle}
          column={yAxisCol}
          tooltipFormatter={tooltipFormatter}
          colors={brandingColors}
          chartRef={chartRef}
        />
      )
    }
    if (savedChartType === 'horizontal-bar') {
      return (
        <BarChart
          key={chartKey}
          {...commonProps}
          onPointClick={pointClick}
          yAxisTitle={computedYAxisTitle}
          column={yAxisCol}
          tooltipFormatter={tooltipFormatter}
          colors={brandingColors}
          chartRef={chartRef}
        />
      )
    }
    if (savedChartType === 'pie-donut')
      return (
        <PieDonutChart
          key={chartKey}
          data={chartData.entries}
          innerSize="30%"
          showLabels={true}
          showLegend={false}
          colors={brandingColors}
          chartRef={chartRef}
          tooltipFormatter={pieTooltipFormatter}
          onPointClick={pointClick}
        />
      )
    if (savedChartType === 'kpi') {
      const kpiCol = columns?.find((c) => c.id === savedKpiValueColumn)
      const kpi = (
        <KpiChart
          key={chartKey}
          value={chartData.value}
          column={kpiCol}
          aggregation={savedKpiAggregation}
        />
      )
      if (!canDrillDown) return kpi
      return (
        <div
          role="button"
          title="Show transactions"
          data-testid="kpi-drilldown"
          onClick={() => setDrilldown({ values: [], labels: [] })}
        >
          {kpi}
        </div>
      )
    }
    if (savedChartType === 'stacked-area') {
      return (
        <StackedAreaChart
          key={chartKey}
          {...commonProps}
          onPointClick={pointClick}
          yAxisTitle={computedYAxisTitle}
          tooltipFormatter={tooltipFormatter}
          colors={brandingColors}
          chartRef={chartRef}
        />
      )
    }
    if (savedChartType === 'pivot-table') {
      // Only render PivotTable if we have valid data structure.
      // Multi-group pivots populate `rows` (tree) with `data` empty; single-group pivots
      // populate both. has_more means more pages exist, so we should still render.
      const pivotData = chartData?.data || {}
      const hasRows = Array.isArray(chartData?.rows) && chartData.rows.length > 0
      const hasFlatData =
        !Array.isArray(pivotData) && Object.keys(pivotData).length > 0
      if (!chartData || (!hasRows && !hasFlatData && !chartData.has_more)) {
        return (
          <div className="text-center p-4">
            <p>No data available</p>
          </div>
        )
      }
      // Get the row column name and display_as from the chart params
      const rowColumn = chart?.params?.pivot_row_column || columns?.find((c) => c.id === savedGroupBy)
      const rowColumnName = rowColumn
        ? resolveColumnName(rowColumn, municipality)
        : 'Row'
      const rowColumnDisplayAs = chart?.params?.pivot_row_column?.display_as
      return (
        <PivotTable
          key={chartKey}
          initialData={pivotData}
          rows={chartData.rows}
          rowColumnMetas={chartData.row_column_metas}
          columnNames={chartData.column_names}
          columnKeys={chartData.column_keys}
          rowColumnName={rowColumnName}
          rowColumnDisplayAs={rowColumnDisplayAs}
          rowColumnType={chartData.row_column_type}
          pivotRowDateGrouping={savedPivotRowDateGrouping}
          pivotRowDateFormat={savedPivotRowDateFormat}
          hasMore={chartData.has_more}
          totalCount={chartData.total_count}
          chartId={chart?.id}
          dataUrl={adapter.chartDataPath?.(chart?.id)}
          dashboardId={dashboardId}
          params={txParams}
          municipality={municipality}
          groupingName={groupingName}
          sortBy={chartData.sort_by}
          sortDirection={chartData.sort_direction}
          inlineGroups={savedPivotInlineGroups}
          totals={chartData.totals}
          onRowClick={canDrillDown ? handlePivotRowClick : null}
        />
      )
    }
    if (savedChartType === 'data-table') {
      const dtData = chartData?.data || []
      const dtColumns = chartData?.columns || []
      if (!dtData.length) {
        return (
          <div className="text-center p-4">
            <p>No data available</p>
          </div>
        )
      }
      return (
        <DataTable
          key={chartKey}
          initialData={dtData}
          columns={dtColumns}
          hasMore={chartData.has_more}
          totalCount={chartData.total_count}
          chartId={chart?.id}
          dataUrl={adapter.chartDataPath?.(chart?.id)}
          params={txParams}
          municipality={municipality}
          groupingName={groupingName}
          sortBy={chartData.sort_by}
          sortDirection={chartData.sort_direction}
          onRowClick={canDrillDown ? handleDataTableRowClick : null}
        />
      )
    }
    if (savedChartType === 'scatter-plot') {
      return (
        <ScatterPlot
          key={chartKey}
          points={chartData}
          xAxisLabel={xAxisCol?.name}
          yAxisLabel={yAxisCol?.name}
          xAxisColumn={xAxisCol}
          yAxisColumn={yAxisCol}
          colors={brandingColors}
          chartRef={chartRef}
          onPointClick={pointClick}
        />
      )
    }
    return null
  }

  if ((savedChartType === 'header' || savedChartType === 'text' || savedChartType === 'page-break') && isPreviewMode) {
    return chart?.id ? <ChartErrorBoundary>{renderChart()}{grainWarningNotice}</ChartErrorBoundary> : null
  }

  const isPageBreak = savedChartType === 'page-break'
  const showBrandingOption = savedChartType !== 'kpi' && savedChartType !== 'pivot-table' && savedChartType !== 'text' && savedChartType !== 'header' && !isPageBreak

  const handleContextMenu = (e) => {
    if (!canEdit || !chart?.id || isPreviewMode) return
    e.preventDefault()
    e.stopPropagation()
    onSelect?.(chart?.id)
  }

  // Double-clicking a chart is a shortcut for select + Edit. Gated on the same edit access as
  // the Edit button, and skipped for page breaks since they have nothing to configure.
  const handleDoubleClick = (e) => {
    if (!canEdit || !chart?.id || isPreviewMode || isPageBreak || isComputed) return
    e.stopPropagation()
    onSelect?.(chart?.id)
    handleOpenModal()
  }

  const showExportInMenu = savedChartType !== 'kpi' && savedChartType !== 'text' && savedChartType !== 'header' && !isPageBreak

  return (
    <Card
      className={isPageBreak ? 'mb-3 border-0 bg-transparent' : 'mb-3'}
      ref={cardRef}
      onClick={(e) => { e.stopPropagation(); onSelect?.(chart?.id) }}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      style={{
        position: 'relative',
        cursor: canEdit && !isPreviewMode ? 'pointer' : 'default',
        ...(isSelected ? { outline: '2px solid #ffc107' } : {})
      }}
    >
      {savedChartType !== 'header' && savedChartType !== 'text' && !isPageBreak && (
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ backgroundColor: 'white' }}
        >
          <span>{savedTitle || 'New Chart'}</span>
          {savedChartFilterCount > 0 && (
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="bottom-end"
              overlay={
                <Popover id={`chart-filters-${chart?.id}`}>
                  <Popover.Body>
                    {savedChartFilterOutline && <ChartFilterSummary outline={savedChartFilterOutline} />}
                  </Popover.Body>
                </Popover>
              }
            >
              {/* A click here is for reading the filters, not for selecting or editing the card. */}
              <AppBadge
                pill
                className="bg-light text-primary border fw-normal"
                aria-label={`Chart filters: ${savedChartFilterDescription}`}
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                data-testid="chart-filters-badge"
              >
                {savedChartFilterCount} chart filter{savedChartFilterCount === 1 ? '' : 's'}
              </AppBadge>
            </OverlayTrigger>
          )}
        </Card.Header>
      )}
      <Card.Body className={isPageBreak ? 'px-0 py-3' : undefined}>
        {chart?.id ? (
          <ChartErrorBoundary>
            {renderChart()}
            {filterCaption && (
              <p className="text-muted small mb-0 mt-2" data-testid="chart-filter-caption">{filterCaption}</p>
            )}
            {grainWarningNotice}
          </ChartErrorBoundary>
        ) : (
          <div className="text-center py-4 text-muted">
            <p>
              {savedChartType === 'header'
                ? 'Configure your header options to see the preview'
                : 'Configure your chart options to see the preview'}
            </p>
          </div>
        )}
      </Card.Body>

      {isSelected && canEdit && !isPreviewMode && chart?.id && (
        <div
          className="d-flex flex-wrap justify-content-end align-items-center gap-1"
          style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 10 }}
          onClick={(e) => e.stopPropagation()}
        >
          {!isPageBreak && !isComputed && (
            <Button
              size="sm"
              variant="light"
              className="border d-flex align-items-center gap-1"
              onClick={handleOpenModal}
            >
              <FontAwesomeIcon icon={faPencilAlt} style={{ fontSize: '0.75rem' }} />
              Edit
            </Button>
          )}
          {showBrandingOption && (
            <Button
              size="sm"
              variant="light"
              className="border d-flex align-items-center gap-1"
              onClick={() => setShowBrandingModal(true)}
            >
              <FontAwesomeIcon icon={faPalette} style={{ fontSize: '0.75rem' }} />
              Colors
            </Button>
          )}
          {showExportInMenu && (
            (savedChartType === 'pivot-table' || savedChartType === 'data-table') ? (
              adapter.allowCsvExport && (
                <Button
                  size="sm"
                  variant="light"
                  className="border d-flex align-items-center gap-1"
                  onClick={handleOpenExportModal}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.75rem' }} />
                  Export to CSV
                </Button>
              )
            ) : (
              <>
                <Button
                  size="sm"
                  variant="light"
                  className="border d-flex align-items-center gap-1"
                  onClick={() => handleExportChart('png')}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.75rem' }} />
                  PNG
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  className="border d-flex align-items-center gap-1"
                  onClick={() => handleExportChart('jpeg')}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.75rem' }} />
                  JPEG
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  className="border d-flex align-items-center gap-1"
                  onClick={() => handleExportChart('svg')}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.75rem' }} />
                  SVG
                </Button>
              </>
            )
          )}
          <Button
            size="sm"
            variant="light"
            className="border d-flex align-items-center gap-1"
            onClick={() => onMoveUp?.()}
            disabled={isFirst || !onMoveUp}
          >
            <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: '0.75rem' }} />
            Move Up
          </Button>
          <Button
            size="sm"
            variant="light"
            className="border d-flex align-items-center gap-1"
            onClick={() => onMoveDown?.()}
            disabled={isLast || !onMoveDown}
          >
            <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: '0.75rem' }} />
            Move Down
          </Button>
          <Button
            size="sm"
            variant="light"
            className="border d-flex align-items-center gap-1 text-danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.75rem' }} />
            Delete
          </Button>
        </div>
      )}

      {/* React bubbles portal events up the tree, so clicks in the drill-down would select or
          edit this card without the wrapper. */}
      {canDrillDown && drilldown && (
        <div onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.stopPropagation()}>
          <Suspense fallback={null}>
            <ChartDrilldownModal
              drilldown={drilldown}
              onHide={() => setDrilldown(null)}
              chartId={chart.id}
              chartTitle={savedTitle}
              chartFilterDescription={savedChartFilterCount > 0 ? savedChartFilterDescription : null}
              dashboardId={dashboardId}
              params={params}
            />
          </Suspense>
        </div>
      )}

      {/* Chart filter editors open in popovers outside the modal; a focus trap would pull focus
          back out of their inputs. */}
      <Modal
        show={showModal}
        onHide={handleCancelModal}
        size="lg"
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{MODAL_TITLES[chartType] || 'Chart Options'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hasSubmitted && Object.keys(validationErrors).length > 0 && (
            <div className="alert alert-danger mb-3">
              <strong>Please fix the following errors:</strong>
              <ul className="mb-0 mt-2">
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          <DataSourceOption
            Form={Form}
            label={dataSourceLabel}
            href={dataSourceHref}
            missing={dataSourceMissing}
            options={showDataSourcePicker ? dataSources : []}
            value={draftDataSourceId}
            onChange={showDataSourcePicker ? setDraftDataSourceId : null}
            helpText={dataSourceHelpText}
          />

          <ChartTypeOption
            Form={Form}
            chartType={chartType}
            chart={chart}
            handleChartTypeChange={handleChartTypeChange}
          />

          {chartType !== 'header' && chartType !== 'text' && chartType !== 'page-break' && (
            <ChartTitleOption
              Form={Form}
              title={title}
              setTitle={setTitle}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              hasSubmitted={hasSubmitted}
            />
          )}

          {/* Chart-specific configuration sections */}
          {(isColumnsSuccess || chartType === 'text' || chartType === 'header' || chartType === 'page-break') &&
            renderChartOptions(chartType, {
              Form,
              Button,
              formState: {
                title,
                xAxisColumn,
                xAxisDateGrouping,
                pivotRowDateGrouping,
                pivotRowSegmentDisplay,
                pivotRowDateFormat,
                yAxisColumn,
                yAxisAggregation,
                yAxisLabel,
                yAxisGroupBy,
                displayLimit,
                showTotals,
                groupBy,
                valueColumn,
                aggregationMethod,
                kpiValueColumn,
                kpiAggregation,
                kpiAnalytic,
                draftColumnAggregations,
                draftAdditionalGroupBys,
                pivotInlineGroups,
                pivotShowTotals,
                draftDataTableColumns,
                textContent,
                headerSubtitle,
                headerBackgroundColor,
                headerShowLogo,
                headerTitleColor,
                headerSubtitleColor,
                headerFontSize,
                headerTextAlign,
                headerShowDivider,
                headerLogoSize,
                headerShowDate,
                headerRepeatOnEveryPage,
                textRepeatOnEveryPage
              },
              setters: {
                setTitle,
                setXAxisColumn,
                setXAxisDateGrouping,
                setPivotRowDateGrouping,
                setPivotRowSegmentDisplay,
                setPivotRowDateFormat,
                setYAxisColumn,
                setYAxisAggregation,
                setYAxisLabel,
                setYAxisGroupBy,
                setDisplayLimit,
                setShowTotals,
                setGroupBy,
                setValueColumn,
                setAggregationMethod,
                setKpiValueColumn,
                setKpiAggregation,
                setKpiAnalytic,
                setDraftColumnAggregations,
                setDraftAdditionalGroupBys,
                setPivotInlineGroups,
                setPivotShowTotals,
                setDraftDataTableColumns,
                setValidationErrors,
                setTextContent,
                setHeaderSubtitle,
                setHeaderBackgroundColor,
                setHeaderShowLogo,
                setHeaderTitleColor,
                setHeaderSubtitleColor,
                setHeaderFontSize,
                setHeaderTextAlign,
                setHeaderShowDivider,
                setHeaderLogoSize,
                setHeaderShowDate,
                setHeaderRepeatOnEveryPage,
                setTextRepeatOnEveryPage
              },
              validationErrors,
              hasSubmitted,
              columns,
              getFilteredColumns,
              getGroupingColumns,
              getValueColumns,
              getAggregationOptions,
              getColumnDisplayName: resolveColumnName,
              getAggregationLabel,
              municipality,
              analytics,
              sectionId: resolvedSectionId,
              municipalitySettings,
              dashboard,
              params
            })}
          {isDataChart && isColumnsSuccess && (
            <div className="mt-3">
              <Button
                variant="light"
                size="sm"
                className={`text-primary${draftChartFilterCount > 0 ? ' border-primary' : ''}`}
                aria-expanded={showChartFilters}
                onClick={() => setShowChartFilters((open) => !open)}
                data-testid="chart-filter-toggle"
              >
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Filter this chart{draftChartFilterCount > 0 && ` (${draftChartFilterCount})`}
              </Button>
              {showChartFilters && (
                <div className="border rounded px-3 pb-3 mt-2" data-testid="chart-filter-panel">
                  <ChartFilterGroups
                    draft={chartFilterDraft}
                    setDraft={setChartFilterDraft}
                    filterColumns={chartFilterColumns}
                    appliedFilters={
                      params?.dashboardId ? appliedViewFilters({ params, columns, customColumns, municipality }) : null
                    }
                    viewGroupFilterCount={countFilters(requestFilterTree(parseFilterTree(params?.filterTree)))}
                    isSample={isSampleSource}
                    canEdit={canEdit}
                    dashboardId={viewDashboardId}
                    showCaption={draftShowFilterCaption}
                    setShowCaption={setDraftShowFilterCaption}
                  />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={handleCancelModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {createMutation.isLoading || updateMutation.isLoading
              ? 'Saving...'
              : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDelete
        id={chart?.id}
        handleDelete={handleConfirmDelete}
        show={showDeleteConfirm}
        setShow={(show) => {
          if (!show) {
            handleCancelDelete()
          }
        }}
        hideButton={true}
        message="Are you sure you want to delete this chart?"
      />
      <ChartBrandingModal
        show={showBrandingModal}
        onHide={() => setShowBrandingModal(false)}
        onSave={handleBrandingSave}
        chartTitle={savedTitle}
        brandingType={savedBrandingType}
        brandingColor={savedBrandingColor}
        municipalitySettings={municipalitySettings}
        isSaving={updateMutation.isLoading}
      />

      {/* Pivot Table Export Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="lead fw-normal">
            <FontAwesomeIcon icon={faDownload} className="text-muted mx-2" />
            {savedChartType === 'data-table' ? 'Export Data Table' : 'Export Pivot Table'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <FieldLabel>Export Name</FieldLabel>
            <Form.Control
              type="text"
              value={exportName}
              onChange={(e) => setExportName(e.target.value)}
              placeholder="Enter export name"
            />
            <Form.Text className="text-muted">
              {savedChartType === 'data-table'
                ? 'The data table will be exported as a CSV file with all rows and columns.'
                : 'The pivot table data will be exported as a CSV file with all rows and columns.'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handlePivotTableExport}>
            Export
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}

export default ChartCard
